import { getLevelsSync } from './levelsService';

// Fallback whitelist — utilisé uniquement si minimumSubPercentage est absent de la réponse API
// (cache serveur ancien ou backend non encore déployé). Peut être retiré une fois le cache expiré.
const VARS_WITH_CATEGORIES_FALLBACK = new Set([
    'ppl', 'cpl', 'cpl-h', 'ppl-h', 'faaaa', 'saron', 'samra', 'fiften', 'iatra', 'fd-ops', 'fd-met',
]);

// Translation map for application labels
const APPLICATION_TRANSLATIONS = {
    'avion':      { fr: 'Avion',       en: 'Airplane'  },
    'helicoptere':{ fr: 'Hélicoptère', en: 'Helicopter'},
    'planeur':    { fr: 'Planeur',     en: 'Glider'    },
    'dispatch':   { fr: 'Dispatch',    en: 'Dispatch'  },
};

function translateApplication(appString) {
    if (!appString) return { fr: '', en: '' };
    const parts = appString.toLowerCase().split(/\s+/);
    const frParts = [];
    const enParts = [];
    for (const part of parts) {
        const match = Object.keys(APPLICATION_TRANSLATIONS).find(key => part.includes(key));
        if (match) {
            frParts.push(APPLICATION_TRANSLATIONS[match].fr);
            enParts.push(APPLICATION_TRANSLATIONS[match].en);
        }
    }
    return {
        fr: frParts.join(' ') || appString,
        en: enParts.join(' ') || appString
    };
}

/**
 * Transform a single raw level/pack row (from API or legacy JSON) into
 * the MODULE_DEFINITIONS shape used throughout the app.
 */
export function transformLevel(level) {
    const isPack = level.isPack ?? false;
    return {
        id:          level.var,
        numericId:   level.idNiveau ?? level.idPack,
        title:       level.libelleFr,
        titleObj:    { fr: level.libelleFr, en: level.libelleEn },
        subtitle:    level.application,
        subtitleObj: translateApplication(level.application),
        newBackgroundColor: level.newBackgroundColor || '',
        description: { fr: level.descriptiveFr ?? '', en: level.descriptiveEn ?? '' },
        newPage:     { fr: level.newPageFr ?? '',    en: level.newPageEn ?? '' },

        // Aliases
        nameFr:        level.libelleFr,
        nameEn:        level.libelleEn,
        descriptionFr: level.descriptiveFr || '',
        descriptionEn: level.descriptiveEn || '',
        abbreviation:  level.libelleFr,
        color:         level.newBackgroundColor || '',
        types: (() => {
            const types = [];
            const appLower = (level.application || '').toLowerCase();
            if (appLower.includes('avion'))                                    types.push('aeroplane');
            if (appLower.includes('helicoptere') || appLower.includes('hélicoptère')) types.push('helicopter');
            if (appLower.includes('planeur'))                                  types.push('glider');
            if (appLower.includes('dispatch'))                                 types.push('dispatch');
            return types.length > 0 ? types : ['aeroplane'];
        })(),
        minimumPercentage: level.minimumPercentage ?? level.MinimumPercentage ?? 60,

        // Flags
        isFree:            level.isFree    ?? false,
        hasCourses:        level.courseAvailable ?? false,
        hasCategories:     !isPack && (
            level.minimumSubPercentage !== undefined
                ? level.minimumSubPercentage > 0
                : VARS_WITH_CATEGORIES_FALLBACK.has(level.var)
        ),
        hasRecommendation: level.hasRecommendation ?? false,
        firstExam:         level.firstExam ?? null,   // { fr, en } — premier examen complet du niveau
        isPack,
        includedLevels:    level.includedLevels ?? [],

        // Pricing
        pricing: level.pricing ?? [],
    };
}

/**
 * Build the full module definitions array from a flat levels array
 * (already merged levels + packs from levelsService).
 */
export function buildModuleDefinitions(levelsArray) {
    return (levelsArray || [])
        .filter(level => level.var !== 'demo' && level.libelleFr !== 'DEMO')
        .map(transformLevel);
}

/**
 * Synchronous getter — returns current cache, or [] if not yet loaded.
 * Use useModules() in React components instead.
 */
function getModuleDefinitionsSync() {
    const cached = getLevelsSync();
    if (!cached) return [];
    return buildModuleDefinitions(cached.levels);
}

/**
 * MODULE_DEFINITIONS — backward-compat live proxy.
 * Delegates all array method calls to the sync cache.
 * Prefer useModules() from LevelsContext in React components.
 */
export const MODULE_DEFINITIONS = new Proxy([], {
    get(_, prop) {
        const live = getModuleDefinitionsSync();
        if (typeof live[prop] === 'function') return live[prop].bind(live);
        return live[prop];
    },
});

export const MOCK_MODULES         = MODULE_DEFINITIONS;
export const MOCK_TRAINING_LEVELS = new Proxy([], {
    get(_, prop) {
        const live = getModuleDefinitionsSync().map(m => ({
            id:       m.id,
            title:    { fr: `${m.titleObj.fr} - ${m.subtitleObj?.fr || m.subtitle}`, en: `${m.titleObj.en} - ${m.subtitleObj?.en || m.subtitle}` },
            color:    m.newBackgroundColor,
            progress: 0,
            available: true,
            modules:  [],
        }));
        if (typeof live[prop] === 'function') return live[prop].bind(live);
        return live[prop];
    },
});

export const MOCK_ORDERS = [
    {
        id: '1001',
        date: '2023-10-15',
        item: 'PACK PPL - Formation Complète',
        price: 149.99,
        currency: 'CAD',
        status: 'completed',
        invoiceUrl: '#'
    }
];

export const MOCK_EXAMS_BY_MODULE = {
    'pstar': [
        {
            id: 'pstar-exam-1',
            title: { fr: 'PSTAR - Examen Officiel', en: 'PSTAR - Official Exam' },
            type: 'exam',
            category: 'full',
            questionsCount: 50,
            duration: 90,
            bestScore: 88,
            status: 'passed'
        }
    ]
};

export const MOCK_QUESTIONS        = {};
export const MOCK_STATS_BY_MODULE  = {};
export const MOCK_STATS            = {};
export const MOCK_EXAM_CATALOG     = [];
export const MOCK_EXAM_RESULTS     = {};
